import React, {
  useMemo,
  useState,
  useEffect,
  useRef,
  useCallback,
} from 'react';

const monthLabels = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

const INCLUDE_TODAY = false;

const IOSPicker = props => {
  const {
    setSelectedDate,
    yearsAhead = 2,
    selectedDate: selectedDateProp,
  } = props;

  const now = new Date();

  const minDate = new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate() + (INCLUDE_TODAY ? 0 : 1),
  );

  const daysInMonth = (y, mIdx) => new Date(y, mIdx + 1, 0).getDate();

  const getInitialDate = dateString => {
    if (dateString) {
      const parsedDate = new Date(`${dateString}T00:00:00`);
      if (!isNaN(parsedDate) && parsedDate >= minDate) {
        return parsedDate;
      }
    }
    return minDate;
  };

  const initialDate = useMemo(
    () => getInitialDate(selectedDateProp),
    [selectedDateProp, minDate],
  );

  const [selectedYear, setSelectedYear] = useState(initialDate.getFullYear());
  const [selectedMonthIndex, setSelectedMonthIndex] = useState(
    initialDate.getMonth(),
  );
  const [selectedDay, setSelectedDay] = useState(initialDate.getDate());
  const [hasInitialScroll, setHasInitialScroll] = useState(false);

  const yearColumnRef = useRef(null);
  const monthColumnRef = useRef(null);
  const dayColumnRef = useRef(null);

  const enableDragScroll = columnRef => {
    const el = columnRef.current;
    if (!el || window.innerWidth < 768) return;

    let isDown = false;
    let startY;
    let scrollTop;

    const onMouseDown = e => {
      if (e.button !== 0) return;
      isDown = true;
      startY = e.pageY;
      scrollTop = el.scrollTop;
    };

    const onMouseUp = () => (isDown = false);

    const onMouseMove = e => {
      if (!isDown) return;
      e.preventDefault();
      const walk = e.pageY - startY;
      el.scrollTop = scrollTop - walk;
    };

    el.addEventListener('mousedown', onMouseDown);
    window.addEventListener('mouseup', onMouseUp);
    window.addEventListener('mousemove', onMouseMove);

    return () => {
      el.removeEventListener('mousedown', onMouseDown);
      window.removeEventListener('mouseup', onMouseUp);
      window.removeEventListener('mousemove', onMouseMove);
    };
  };

  useEffect(() => {
    const cleanups = [
      enableDragScroll(yearColumnRef),
      enableDragScroll(monthColumnRef),
      enableDragScroll(dayColumnRef),
    ];
    return () => cleanups.forEach(fn => fn && fn());
  }, []);

  // --- Years
  const years = useMemo(() => {
    const arr = [];
    for (
      let y = minDate.getFullYear();
      y <= minDate.getFullYear() + yearsAhead;
      y++
    )
      arr.push(y);
    return arr;
  }, [minDate, yearsAhead]);

  // --- Months
  const months = useMemo(() => {
    const startIdx =
      selectedYear === minDate.getFullYear() ? minDate.getMonth() : 0;
    return Array.from({ length: 12 - startIdx }, (_, i) => startIdx + i);
  }, [selectedYear, minDate]);

  // --- Days
  const days = useMemo(() => {
    const total = daysInMonth(selectedYear, selectedMonthIndex);
    const startDay =
      selectedYear === minDate.getFullYear() &&
      selectedMonthIndex === minDate.getMonth()
        ? minDate.getDate()
        : 1;
    return Array.from(
      { length: total - (startDay - 1) },
      (_, i) => startDay + i,
    );
  }, [selectedYear, selectedMonthIndex, minDate]);

  // --- Find center element
  const findCenterElement = columnElement => {
    if (!columnElement) return null;
    const items = columnElement.querySelectorAll('.picker-item');
    const columnRect = columnElement.getBoundingClientRect();
    const columnCenter = columnRect.top + columnRect.height / 2;

    let closestElement = null;
    let closestDistance = Infinity;

    items.forEach(item => {
      const itemRect = item.getBoundingClientRect();
      const itemCenter = itemRect.top + itemRect.height / 2;
      const distance = Math.abs(itemCenter - columnCenter);
      if (distance < closestDistance) {
        closestDistance = distance;
        closestElement = item;
      }
    });

    return closestElement;
  };

  const handleScroll = useCallback(
    (columnRef, setSelected, items, getValue) => {
      const centerElement = findCenterElement(columnRef.current);
      if (centerElement) {
        const index = Array.from(
          columnRef.current.querySelectorAll('.picker-item'),
        ).indexOf(centerElement);
        if (index !== -1 && items[index] !== undefined) {
          setSelected(getValue(items[index]));
        }
      }
    },
    [],
  );

  // --- Add listeners
  useEffect(() => {
    const handleYearScroll = () =>
      handleScroll(yearColumnRef, setSelectedYear, years, y => y);
    const handleMonthScroll = () =>
      handleScroll(monthColumnRef, setSelectedMonthIndex, months, m => m);
    const handleDayScroll = () =>
      handleScroll(dayColumnRef, setSelectedDay, days, d => d);

    yearColumnRef.current?.addEventListener('scroll', handleYearScroll);
    monthColumnRef.current?.addEventListener('scroll', handleMonthScroll);
    dayColumnRef.current?.addEventListener('scroll', handleDayScroll);

    return () => {
      yearColumnRef.current?.removeEventListener('scroll', handleYearScroll);
      monthColumnRef.current?.removeEventListener('scroll', handleMonthScroll);
      dayColumnRef.current?.removeEventListener('scroll', handleDayScroll);
    };
  }, [years, months, days, handleScroll]);

  // --- Scroll inicial
  useEffect(() => {
    if (hasInitialScroll) return;
    const scrollToElement = (ref, index) => {
      const items = ref.current?.querySelectorAll('.picker-item');
      items?.[index]?.scrollIntoView({ block: 'center' });
    };

    scrollToElement(yearColumnRef, years.indexOf(selectedYear));
    scrollToElement(monthColumnRef, months.indexOf(selectedMonthIndex));
    scrollToElement(dayColumnRef, days.indexOf(selectedDay));
    setHasInitialScroll(true);
  }, [
    years,
    months,
    days,
    selectedYear,
    selectedMonthIndex,
    selectedDay,
    hasInitialScroll,
  ]);

  // --- Actualizar Redux
  useEffect(() => {
    const pad = n => String(n).padStart(2, '0');
    const formattedDate = `${selectedYear}-${pad(selectedMonthIndex + 1)}-${pad(selectedDay)}`;
    if (formattedDate !== selectedDateProp) setSelectedDate(formattedDate);
  }, [
    selectedYear,
    selectedMonthIndex,
    selectedDay,
    selectedDateProp,
    setSelectedDate,
  ]);

  const renderColumn = (
    items,
    selected,
    setSelected,
    columnRef,
    renderLabel = x => x,
    getValue = x => x,
  ) => (
    <div className="picker-column" ref={columnRef}>
      {items.map((item, idx) => {
        const value = getValue(item);
        return (
          <div
            key={idx}
            className={`picker-item ${value === selected ? 'selected' : ''}`}
            onClick={() => setSelected(value)}
          >
            {renderLabel(item)}
          </div>
        );
      })}
    </div>
  );

  return (
    <div className="picker-container">
      {renderColumn(years, selectedYear, setSelectedYear, yearColumnRef)}
      {renderColumn(
        months,
        selectedMonthIndex,
        setSelectedMonthIndex,
        monthColumnRef,
        m => monthLabels[m],
      )}
      {renderColumn(days, selectedDay, setSelectedDay, dayColumnRef)}
    </div>
  );
};

export default IOSPicker;
